import { useCallback, useEffect, useState } from "react";
import {
  getManufacturingDealCogsSnapshot,
  getManufacturingDealFullCogsPreview,
  type MfgDealCogsSnapshotRow,
} from "@/lib/mfgOrdersClient";
import { getOpsDeal } from "@/lib/opsPaperworkClient";
import {
  deriveOpsLookupCostRollup,
  type OpsLookupCostRollup,
} from "@/lib/opsLookup/opsLookupCosts";

const EMPTY: OpsLookupCostRollup = {
  theoreticalUsd: null,
  actualUsd: null,
  varianceUsd: null,
  retailUsd: null,
  dealerCostUsd: null,
  catalogWholesaleUsd: null,
  theoreticalSource: null,
  actualSource: null,
  dealerCostSource: null,
};

/**
 * Loads sale/build COGS snapshots (+ preview fallback) and deal metadata for
 * dealer_cost_usd when a deal is in focus.
 */
export function useOpsLookupCosts(dealId: string | null) {
  const [rollup, setRollup] = useState<OpsLookupCostRollup>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    const [saleRes, buildRes, dealRes] = await Promise.all([
      getManufacturingDealCogsSnapshot(id, "sale_commit"),
      getManufacturingDealCogsSnapshot(id, "build_commit"),
      getOpsDeal(id),
    ]);

    let sale: MfgDealCogsSnapshotRow | null = null;
    let build: MfgDealCogsSnapshotRow | null = null;
    let preview: MfgDealCogsSnapshotRow | null = null;
    let dealMetadata: Record<string, unknown> | null = null;
    const soft: string[] = [];

    if (saleRes.ok) sale = saleRes.data.snapshot;
    else soft.push(saleRes.error || "Sale COGS unavailable");

    if (buildRes.ok) build = buildRes.data.snapshot;
    else soft.push(buildRes.error || "Build COGS unavailable");

    if (dealRes.ok) {
      const deal = (dealRes.data as { deal?: Record<string, unknown> }).deal;
      const meta = deal?.metadata;
      if (meta && typeof meta === "object" && !Array.isArray(meta)) {
        dealMetadata = meta as Record<string, unknown>;
      }
    }

    if (!sale?.full_cost_usd) {
      const previewRes = await getManufacturingDealFullCogsPreview(id);
      if (previewRes.ok) preview = previewRes.data.preview;
    }

    setRollup(
      deriveOpsLookupCostRollup({
        saleSnapshot: sale,
        buildSnapshot: build,
        preview,
        dealMetadata,
      }),
    );
    setLoading(false);
    setError(soft.length && !sale && !build && !preview ? soft.join(" · ") : null);
  }, []);

  useEffect(() => {
    if (!dealId) {
      setRollup(EMPTY);
      setError(null);
      setLoading(false);
      return;
    }
    void reload(dealId);
  }, [dealId, reload]);

  return { rollup, loading, error, reload };
}
